import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { LocationRow, PhotoRow, PlantRow } from "@garden/types/db";
import { supabase } from "../auth";
import { listUntaggedPhotos, markPhotoTagged } from "../db/photos";
import {
  createPhotoTag,
  listRecentlyTaggedPlantIds,
  plantHasAnyPhoto,
} from "../db/photoTags";
import { listPlants } from "../db/plants";
import { listLocations } from "../db/locations";
import { usePhotoUrl } from "../lib/photoServe";
import { useCurrentGardenId } from "../lib/useCurrentGarden";
import "./Tag.css";

type Choice =
  | { kind: "plant"; plantId: string }
  | { kind: "location"; locationId: string }
  | { kind: "newPlant" };

export function Tag() {
  const navigate = useNavigate();
  const routerLocation = useLocation();

  // When returning from /plants/new the location.state carries the
  // freshly-created plant id and the photo we should attach to it. We
  // perform that tag immediately, then advance the queue.
  type ReturnState = { taggedPhotoId?: string; createdPlantId?: string };
  const returnState = (routerLocation.state as ReturnState | null) ?? null;

  const [photos, setPhotos] = useState<PhotoRow[] | null>(null);
  const [index, setIndex] = useState(0);
  const [plants, setPlants] = useState<PlantRow[]>([]);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [recentPlantIds, setRecentPlantIds] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [choice, setChoice] = useState<Choice | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gardenId = useCurrentGardenId();

  // Initial load. If we just returned from /plants/new, finish the
  // handshake (tag the new plant, mark photo tagged) BEFORE listing the
  // untagged queue — otherwise the just-tagged photo could flash back into
  // the list depending on which fetch resolves first.
  useEffect(() => {
    if (!supabase || !gardenId) return;
    let active = true;
    (async () => {
      try {
        if (returnState?.taggedPhotoId && returnState.createdPlantId) {
          await createPhotoTag(supabase!, {
            photo_id: returnState.taggedPhotoId,
            subject_type: "plant",
            subject_id: returnState.createdPlantId,
            is_primary: true, // freshly-created plant has no other photos yet
          });
          await markPhotoTagged(supabase!, returnState.taggedPhotoId);
          // Clear router state so a back-nav can't re-fire this branch.
          navigate("/tag", { replace: true, state: null });
        }

        const [ps, pls, locs, recent] = await Promise.all([
          listUntaggedPhotos(supabase!, gardenId),
          listPlants(supabase!, gardenId),
          listLocations(supabase!, gardenId),
          listRecentlyTaggedPlantIds(supabase!, 5, gardenId),
        ]);
        if (!active) return;
        setPhotos(ps);
        setPlants(pls.filter((p) => p.status !== "removed"));
        setLocations(locs);
        setRecentPlantIds(recent);
      } catch (err) {
        if (!active) return;
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gardenId]);

  const total = photos?.length ?? 0;
  const photo = photos && index < total ? photos[index] : null;
  const finished = photos !== null && total === 0;
  // User skipped past the last untagged photo without tagging any.
  // Photos remain untagged on the server (correct per plan); we just
  // hand control back to /today.
  const skippedToEnd = photos !== null && total > 0 && index >= total;

  // Reset selection state when moving to a new photo.
  useEffect(() => {
    setChoice(null);
    setQuery("");
  }, [photo?.id]);

  const trimmedQuery = query.trim().toLowerCase();
  const matchesQuery = (s: string | null | undefined) =>
    s ? s.toLowerCase().includes(trimmedQuery) : false;

  const filteredPlants = useMemo(() => {
    if (!trimmedQuery) {
      // No query: show recent first, capped at 6.
      const byId = new Map(plants.map((p) => [p.id, p] as const));
      const recent = recentPlantIds
        .map((id) => byId.get(id))
        .filter((p): p is PlantRow => !!p);
      const seen = new Set(recent.map((p) => p.id));
      const fillers = plants
        .filter((p) => !seen.has(p.id))
        .slice(0, Math.max(0, 6 - recent.length));
      return [...recent, ...fillers];
    }
    return plants.filter(
      (p) =>
        matchesQuery(p.common_name) ||
        matchesQuery(p.scientific_name) ||
        matchesQuery(p.cultivar),
    );
  }, [plants, recentPlantIds, trimmedQuery]);

  const filteredLocations = useMemo(() => {
    if (!trimmedQuery) return locations.slice(0, 4);
    return locations.filter((l) => matchesQuery(l.name));
  }, [locations, trimmedQuery]);

  const locationsById = useMemo(
    () => new Map(locations.map((l) => [l.id, l])),
    [locations],
  );

  const skip = () => {
    setIndex((i) => i + 1);
  };

  const cancelToToday = () => {
    navigate("/today");
  };

  const onSave = async () => {
    if (!supabase || !photo || !choice) return;
    setSaving(true);
    setError(null);
    try {
      if (choice.kind === "plant") {
        const isPrimary = !(await plantHasAnyPhoto(supabase, choice.plantId));
        await createPhotoTag(supabase, {
          photo_id: photo.id,
          subject_type: "plant",
          subject_id: choice.plantId,
          is_primary: isPrimary,
        });
        await markPhotoTagged(supabase, photo.id);
      } else if (choice.kind === "location") {
        await createPhotoTag(supabase, {
          photo_id: photo.id,
          subject_type: "location",
          subject_id: choice.locationId,
          is_primary: false,
        });
        await markPhotoTagged(supabase, photo.id);
      }
      // Drop tagged photo from queue, advance to next.
      setPhotos((curr) => (curr ? curr.filter((p) => p.id !== photo.id) : curr));
      setIndex(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const onAddNewPlant = () => {
    if (!photo) return;
    navigate("/plants/new", {
      state: { fromTagPhotoId: photo.id, returnTo: "/tag" },
    });
  };

  if (!photos) {
    return <div className="Tag__loading">Loading…</div>;
  }

  if (finished) {
    return (
      <div className="Tag Tag--done">
        <div className="Tag__doneCard">
          <div className="Tag__doneCheck" aria-hidden="true">✓</div>
          <h1>All caught up.</h1>
          <p>No untagged photos left.</p>
          <button type="button" className="Tag__doneBtn" onClick={cancelToToday}>
            Back to today
          </button>
        </div>
      </div>
    );
  }

  if (skippedToEnd) {
    return (
      <div className="Tag Tag--done">
        <div className="Tag__doneCard">
          <h1>End of queue.</h1>
          <p>
            {total === 1
              ? "1 photo still untagged. Come back when you know what it is."
              : `${total} photos still untagged. Come back when you know what they are.`}
          </p>
          <button type="button" className="Tag__doneBtn" onClick={cancelToToday}>
            Back to today
          </button>
        </div>
      </div>
    );
  }

  if (!photo) {
    return <div className="Tag__loading">Loading…</div>;
  }

  return (
    <div className="Tag">
      <header className="Tag__top">
        <div>
          <div className="Tag__topTitle">Tag photos</div>
          <div className="Tag__topMeta">
            {index + 1} of {total}
            {photo.taken_date ? ` · ${formatDate(photo.taken_date)}` : ""}
          </div>
        </div>
        <button type="button" className="Tag__cancel" onClick={cancelToToday}>
          Cancel
        </button>
      </header>

      <PhotoFrame
        r2Key={photo.r2_key}
        counter={`${index + 1} / ${total}`}
        meta={photo.taken_date ? formatDate(photo.taken_date) : "Recent capture"}
        onSkip={skip}
      />

      <div className="Tag__body">
        <label className="Tag__search">
          <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <circle cx="6" cy="6" r="4" />
            <path d="M9 9l3 3" />
          </svg>
          <input
            type="search"
            placeholder="Search a plant or location"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>

        {filteredPlants.length > 0 && (
          <>
            <div className="Tag__sectionH">
              {trimmedQuery ? "Plants" : "Suggested · recently tagged"}
            </div>
            {filteredPlants.map((p) => {
              const loc = p.location_id ? locationsById.get(p.location_id) : null;
              const selected =
                choice?.kind === "plant" && choice.plantId === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  className={`Tag__row${selected ? " Tag__row--selected" : ""}`}
                  onClick={() =>
                    setChoice(selected ? null : { kind: "plant", plantId: p.id })
                  }
                  aria-pressed={selected}
                >
                  <span
                    className={`Tag__rowPin Tag__rowPin--${pinClass(p)}`}
                    aria-hidden="true"
                  />
                  <span className="Tag__rowBody">
                    <span className="Tag__rowName">
                      {p.common_name}
                      {p.cultivar ? <em> '{p.cultivar}'</em> : null}
                    </span>
                    <span className="Tag__rowMeta">
                      {loc ? loc.name : "No location"}
                      {p.scientific_name ? ` · ${p.scientific_name}` : ""}
                    </span>
                  </span>
                  <span className="Tag__rowCheck" aria-hidden="true" />
                </button>
              );
            })}
          </>
        )}

        {filteredLocations.length > 0 && (
          <>
            <div className="Tag__sectionH">
              {trimmedQuery ? "Locations" : "Or tag a location"}
            </div>
            {filteredLocations.map((l) => {
              const selected =
                choice?.kind === "location" && choice.locationId === l.id;
              return (
                <button
                  key={l.id}
                  type="button"
                  className={`Tag__row${selected ? " Tag__row--selected" : ""}`}
                  onClick={() =>
                    setChoice(
                      selected ? null : { kind: "location", locationId: l.id },
                    )
                  }
                  aria-pressed={selected}
                >
                  <span
                    className="Tag__rowPin Tag__rowPin--neutral"
                    aria-hidden="true"
                  />
                  <span className="Tag__rowBody">
                    <span className="Tag__rowName Tag__rowName--plain">
                      {l.name}
                    </span>
                    <span className="Tag__rowMeta">
                      {l.kind} · location only
                    </span>
                  </span>
                  <span className="Tag__rowCheck" aria-hidden="true" />
                </button>
              );
            })}
          </>
        )}

        <div className="Tag__sectionH">Or</div>
        <button
          type="button"
          className="Tag__row Tag__row--action"
          onClick={onAddNewPlant}
        >
          <span className="Tag__rowPin Tag__rowPin--clay" aria-hidden="true" />
          <span className="Tag__rowBody">
            <span className="Tag__rowName Tag__rowName--plain">
              Add new plant
            </span>
            <span className="Tag__rowMeta">
              Create a plant and tag this photo to it
            </span>
          </span>
          <span className="Tag__rowCheck Tag__rowCheck--arrow" aria-hidden="true">
            ›
          </span>
        </button>

        {filteredPlants.length === 0 &&
          filteredLocations.length === 0 &&
          trimmedQuery && (
            <div className="Tag__noResults">
              No plants or locations match "{query}". Try "Add new plant".
            </div>
          )}
      </div>

      {error && <div className="Tag__error">{error}</div>}

      <div className="Tag__bottom">
        <button
          type="button"
          className="Tag__btnSecondary"
          onClick={skip}
          disabled={saving}
        >
          Skip
        </button>
        <button
          type="button"
          className="Tag__btnPrimary"
          onClick={onSave}
          disabled={!choice || choice.kind === "newPlant" || saving}
        >
          {saving ? "Saving…" : "Save & next"}
          <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M5 3l4 4-4 4" />
          </svg>
        </button>
      </div>
    </div>
  );
}

interface PhotoFrameProps {
  r2Key: string;
  counter: string;
  meta: string;
  onSkip: () => void;
}

function PhotoFrame({ r2Key, counter, meta, onSkip }: PhotoFrameProps) {
  const { url, error } = usePhotoUrl(r2Key);
  return (
    <div className="Tag__photo">
      {url ? (
        <img className="Tag__photoImg" src={url} alt="" />
      ) : (
        <div className="Tag__photoPlaceholder">{error ?? "Loading photo…"}</div>
      )}
      <span className="Tag__photoCounter">{counter}</span>
      <span className="Tag__photoMeta">{meta}</span>
      <button type="button" className="Tag__photoSkip" onClick={onSkip}>
        Skip →
      </button>
    </div>
  );
}

function pinClass(p: PlantRow): string {
  switch (p.symbol) {
    case "rose":
    case "lavender":
    case "foxglove":
      return "rose";
    case "tree":
    case "tree_conifer":
    case "tree_fruit":
    case "shrub":
    case "shrub_topiary":
    case "bamboo":
    case "fern":
      return "moss";
    case "sunflower":
    case "tulip":
    case "daisy":
      return "honey";
    case "veg":
    case "carrot":
    case "tomato":
    case "herb":
      return "veg";
    case "vine":
    case "climber":
      return "sage";
    case "container":
      return "clay";
    default:
      return "terracotta";
  }
}

function formatDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

